// components/ui/CustomSelect.jsx
import React, { useState, useRef, useEffect } from "react";

// Simple Chevron Down Icon
const ChevronDownIcon = () => (
  <svg
    className="w-5 h-5 text-gray-400"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

// Optional Check Icon for selected item (Example)
// const CheckIcon = () => (
//   <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
//     <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
//   </svg>
// );

function CustomSelect({ label, options, value, onChange, name }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectRef]);

  const handleSelectOption = (optionValue) => {
    // Mimic event object structure for the parent's handleChange
    onChange({ target: { name: name, value: optionValue } });
    setIsOpen(false);
  };

  // Find the label of the currently selected option for display
  const selectedOptionLabel =
    options.find((opt) => opt.value === value)?.label ||
    options[0]?.label ||
    "";

  return (
    <div className="relative" ref={selectRef}>
      {/* Label */}
      <label
        // Optional: link with id if needed: htmlFor={`${name}-button`}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>

      {/* Button acting as the select input */}
      <button
        type="button"
        // Optional: id={`${name}-button`}
        className="relative w-full cursor-default rounded-md border border-gray-300 bg-white p-3 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="block truncate">{selectedOptionLabel}</span>
        <span className="pointer-events-none inset-y-0 right-0 flex items-center">
          <ChevronDownIcon />
        </span>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute z-10 mt-1 w-full rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden py-1" // Added rounded-xl and py-1 for padding
          role="listbox"
          aria-orientation="vertical"
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`
                text-gray-900 cursor-pointer select-none relative py-2 pl-4 pr-4 m-1 text-sm
                transition-colors duration-150 ease-in-out
                hover:bg-emerald-800 hover:text-white hover:rounded-lg  /* Hover styles */
                ${
                  option.value === value
                    ? "font-semibold bg-gray-50"
                    : "font-normal"
                } /* Indicate selected slightly */
              `}
              onClick={() => handleSelectOption(option.value)}
              role="option"
              aria-selected={option.value === value}
            >
              <span
                className={`block truncate ${
                  option.value === value ? "text-emerald-900" : ""
                }`}
              >
                {" "}
                {/* Style selected text if needed */}
                {option.label}
              </span>

              {/* Optional: Checkmark for selected item */}
              {/* {option.value === value && (
                 <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-emerald-600">
                   <CheckIcon />
                 </span>
               )} */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CustomSelect;
